/**
 * Club Dashboard — Full-featured club/gym management dashboard.
 * For org_pro (Club) tier users.
 *
 * Sections:
 * - Location filter (franchise/multi-location)
 * - Gym Profile card
 * - Club Overview stats
 * - Announcements
 * - Challenge Ideas
 * - Popular Exercises
 * - Member List with search + sort + CSV export
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  Platform,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  ChevronLeft, Download, Users, TrendingUp, BarChart3,
  Trophy, Swords, Search, UserCheck, UserX, MapPin, Send, Pencil,
  Target, Award, Calendar, Star, Plus,
} from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';

/* ── Types ─────────────────────────────────────────────────── */

interface ClubMember {
  id: string;
  handle: string | null;
  display_name: string | null;
  gender: string | null;
  bodyweight: number | null;
  units: string | null;
  lifting_score: number | null;
  duel_standard_strength_kg: number | null;
  created_at: string;
  lastWorkoutDate: string | null;
  workoutCount: number;
  totalVolume: number;
  home_gym_name: string | null;
  home_gym_address: string | null;
}

interface ClubStats {
  totalMembers: number;
  activeThisWeek: number;
  activeThisMonth: number;
  totalWorkouts: number;
  totalVolume: number;
  avgStrengthScore: number;
  topExercises: { name: string; count: number }[];
}

interface GymLocation {
  name: string;
  address: string;
  key: string;
}

interface Announcement {
  id: string;
  text: string;
  timestamp: Date;
}

const CHALLENGE_TEMPLATES = [
  { title: 'Most Volume This Month', reward: 'Reward: Free month membership', icon: BarChart3 },
  { title: 'Most Duels Won This Month', reward: 'Reward: Free personal training session', icon: Swords },
  { title: 'Highest Strength Score Improvement', reward: 'Reward: Free merchandise', icon: TrendingUp },
  { title: 'Most Consistent (Most Workout Days)', reward: 'Reward: Free month membership', icon: Calendar },
  { title: 'New Member Challenge: Complete 10 Workouts', reward: 'Reward: Free shake/supplement', icon: Star },
] as const;

/* ── Announcements List (max 3, expandable) ────────────────── */

function AnnouncementList({ announcements, colors: c }: { announcements: Announcement[]; colors: Record<string, string> }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? announcements : announcements.slice(0, 3);
  return (
    <View style={{ marginTop: 14, gap: 8 }}>
      {visible.map(a => (
        <View key={a.id} style={{ borderTopWidth: 1, borderColor: c.border, paddingTop: 10 }}>
          <Text style={{ fontSize: 14, fontWeight: '500', color: c.text, marginBottom: 4 }}>{a.text}</Text>
          <Text style={{ fontSize: 11, fontWeight: '500', color: c.textTertiary }}>
            {a.timestamp.toLocaleString()}
          </Text>
        </View>
      ))}
      {announcements.length > 3 && !expanded && (
        <TouchableOpacity onPress={() => setExpanded(true)}>
          <Text style={{ fontSize: 13, fontWeight: '700', color: c.accent, textAlign: 'center', paddingVertical: 8 }}>
            See {announcements.length - 3} more
          </Text>
        </TouchableOpacity>
      )}
      {expanded && announcements.length > 3 && (
        <TouchableOpacity onPress={() => setExpanded(false)}>
          <Text style={{ fontSize: 13, fontWeight: '700', color: c.accent, textAlign: 'center', paddingVertical: 8 }}>
            Show less
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

/* ── Component ─────────────────────────────────────────────── */

export default function ClubDashboard() {
  const router = useRouter();
  const { theme } = useTheme();
  const { profile } = useAuth();
  const c = theme.colors;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [clubName, setClubName] = useState('Your Club');
  const [clubAddress, setClubAddress] = useState('');
  const [members, setMembers] = useState<ClubMember[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'strength' | 'volume' | 'workouts' | 'name'>('strength');
  const [exporting, setExporting] = useState(false);

  // Franchise / location
  const [locations, setLocations] = useState<GymLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('all'); // 'all' or location key

  // Announcements
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [announcementText, setAnnouncementText] = useState('');

  // Workout data keyed by user id
  const [workoutsByUser, setWorkoutsByUser] = useState<Map<string, any[]>>(new Map());

  const gymId = profile?.gym_id || (profile as any)?.home_gym_id || null;
  const gymName = profile?.home_gym_name || null;

  /* ── Data Loading ──────────────────────────────────────── */

  const MEMBER_SELECT = 'id, handle, display_name, gender, bodyweight, units, lifting_score, duel_standard_strength_kg, created_at, home_gym_name, home_gym_address';

  const loadData = useCallback(async () => {
    if (!gymId && !gymName) { setLoading(false); return; }
    setLoading(true);

    try {
      if (profile?.home_gym_name) setClubName(profile.home_gym_name);
      if ((profile as any)?.home_gym_address) setClubAddress((profile as any).home_gym_address);

      // Fetch all members in this gym — try gym_id first, fallback to gym name match
      let gymMembers: any[] | null = null;

      if (gymId) {
        const { data, error } = await supabase
          .from('profiles')
          .select(MEMBER_SELECT)
          .or(`gym_id.eq.${gymId},home_gym_id.eq.${gymId}`)
          .order('lifting_score', { ascending: false });
        if (error) console.warn('[ClubDash] gym_id query error:', error.message, error.code);
        gymMembers = data;
      }

      // Fallback: match by gym name if no ID match found members
      if ((!gymMembers || gymMembers.length === 0) && gymName) {
        const { data, error } = await supabase
          .from('profiles')
          .select(MEMBER_SELECT)
          .eq('home_gym_name', gymName)
          .order('lifting_score', { ascending: false });
        if (error) console.warn('[ClubDash] gym_name query error:', error.message, error.code);
        gymMembers = data;
      }

      // Fallback 2: if still no members, try loading ALL profiles (admin-like) to diagnose
      if ((!gymMembers || gymMembers.length === 0)) {
        console.warn('[ClubDash] No members found via gym_id or gym_name. Trying all profiles...');
        const { data, error } = await supabase
          .from('profiles')
          .select(MEMBER_SELECT)
          .not('handle', 'is', null)
          .order('lifting_score', { ascending: false })
          .limit(50);
        if (error) {
          console.warn('[ClubDash] ALL profiles query error:', error.message, error.code);
          setLoadError(`Cannot read profiles: ${error.message}`);
        } else {
          // loaded fallback profiles
          gymMembers = data;
        }
      }

      const memberList = gymMembers || [];

      // Derive locations from unique gym name + address combos
      const locMap = new Map<string, GymLocation>();
      memberList.forEach((m: any) => {
        const name = m.home_gym_name || clubName;
        const addr = m.home_gym_address || '';
        const key = `${name}|||${addr}`;
        if (!locMap.has(key)) locMap.set(key, { name, address: addr, key });
      });
      setLocations(Array.from(locMap.values()));

      // Fetch workout data
      const memberIds = memberList.map((m: any) => m.id);
      const { data: workouts } = memberIds.length > 0
        ? await supabase.from('workouts').select('user_id, performed_at, payload').in('user_id', memberIds)
        : { data: [] };

      const wByUser = new Map<string, any[]>();
      (workouts || []).forEach((w: any) => {
        const list = wByUser.get(w.user_id) || [];
        list.push(w);
        wByUser.set(w.user_id, list);
      });
      setWorkoutsByUser(wByUser);

      // Enrich members
      const enriched: ClubMember[] = memberList.map((m: any) => {
        const userWorkouts = wByUser.get(m.id) || [];
        let vol = 0;
        let lastDate: string | null = null;

        userWorkouts.forEach((w: any) => {
          if (!lastDate || w.performed_at > lastDate) lastDate = w.performed_at;
          const payload = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
          (payload?.exercises ?? []).forEach((ex: any) => {
            (ex.sets ?? []).forEach((s: any) => {
              vol += (Number(s.weight) || 0) * (Number(s.reps) || 0);
            });
          });
        });

        return {
          id: m.id,
          handle: m.handle,
          display_name: m.display_name,
          gender: m.gender,
          bodyweight: m.bodyweight,
          units: m.units,
          lifting_score: m.lifting_score,
          duel_standard_strength_kg: m.duel_standard_strength_kg,
          created_at: m.created_at,
          lastWorkoutDate: lastDate,
          workoutCount: userWorkouts.length,
          totalVolume: vol,
          home_gym_name: m.home_gym_name,
          home_gym_address: m.home_gym_address,
        };
      });

      setMembers(enriched);
    } catch (err: any) {
      console.error('Club dashboard error:', err);
      setLoadError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [gymId, gymName, profile]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Derived: filtered by location, then compute stats ── */

  const locationMembers = useMemo(() => {
    if (selectedLocation === 'all') return members;
    return members.filter(m => {
      const key = `${m.home_gym_name || clubName}|||${m.home_gym_address || ''}`;
      return key === selectedLocation;
    });
  }, [members, selectedLocation, clubName]);

  const stats = useMemo<ClubStats | null>(() => {
    if (members.length === 0 && !loading) return null;
    const list = locationMembers;
    const now = Date.now();
    const weekAgo = now - 7 * 86400000;
    const monthAgo = now - 30 * 86400000;

    let totalVol = 0;
    let totalW = 0;
    const exerciseCounter = new Map<string, number>();
    const activeWeek = new Set<string>();
    const activeMonth = new Set<string>();

    list.forEach(m => {
      totalVol += m.totalVolume;
      totalW += m.workoutCount;

      const userWorkouts = workoutsByUser.get(m.id) || [];
      userWorkouts.forEach((w: any) => {
        const t = new Date(w.performed_at).getTime();
        if (t >= weekAgo) activeWeek.add(m.id);
        if (t >= monthAgo) activeMonth.add(m.id);
        const payload = typeof w.payload === 'string' ? JSON.parse(w.payload) : w.payload;
        (payload?.exercises ?? []).forEach((ex: any) => {
          exerciseCounter.set(ex.name, (exerciseCounter.get(ex.name) || 0) + 1);
        });
      });
    });

    const scores = list.map(m => m.duel_standard_strength_kg ?? m.lifting_score ?? 0).filter(s => s > 0);
    const avg = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;

    const topEx = Array.from(exerciseCounter.entries())
      .sort(([, a], [, b]) => b - a).slice(0, 10)
      .map(([name, count]) => ({ name, count }));

    return {
      totalMembers: list.length,
      activeThisWeek: activeWeek.size,
      activeThisMonth: activeMonth.size,
      totalWorkouts: totalW,
      totalVolume: totalVol,
      avgStrengthScore: avg,
      topExercises: topEx,
    };
  }, [locationMembers, workoutsByUser, loading, members.length]);

  const filteredMembers = useMemo(() =>
    locationMembers
      .filter(m => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (m.display_name?.toLowerCase().includes(q) ||
          m.handle?.toLowerCase().includes(q));
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'strength': return (b.duel_standard_strength_kg ?? b.lifting_score ?? 0) - (a.duel_standard_strength_kg ?? a.lifting_score ?? 0);
          case 'volume': return b.totalVolume - a.totalVolume;
          case 'workouts': return b.workoutCount - a.workoutCount;
          case 'name': return (a.display_name || '').localeCompare(b.display_name || '');
          default: return 0;
        }
      }),
    [locationMembers, searchQuery, sortBy]);

  /* ── Helpers ────────────────────────────────────────────── */

  const formatNum = (n: number): string => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${Math.round(n / 1000)}K`;
    return String(n);
  };

  const getMemberName = (m: ClubMember) =>
    m.display_name || m.handle || 'Member';

  const handleExport = async () => {
    setExporting(true);
    try {
      const headers = ['Name', 'Handle', 'Gender', 'Bodyweight', 'Units', 'Strength Score', 'Lifting Score', 'Workouts', 'Total Volume', 'Last Workout', 'Joined', 'Location'];
      const rows = filteredMembers.map(m => [
        m.display_name || m.handle || '',
        m.handle || '',
        m.gender || '',
        m.bodyweight ?? '',
        m.units || '',
        m.duel_standard_strength_kg ?? '',
        m.lifting_score ?? '',
        m.workoutCount,
        m.totalVolume,
        m.lastWorkoutDate ? new Date(m.lastWorkoutDate).toLocaleDateString() : '',
        new Date(m.created_at).toLocaleDateString(),
        m.home_gym_name || '',
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));

      const csv = [headers.join(','), ...rows].join('\n');
      if (Platform.OS === 'web') {
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${clubName.replace(/\s/g, '-').toLowerCase()}-members-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
      Alert.alert('Export Complete', 'Members exported successfully.');
    } catch (err: any) {
      Alert.alert('Export Failed', "Couldn't export members. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleSendAnnouncement = () => {
    const text = announcementText.trim();
    if (!text) return;
    setAnnouncements(prev => [
      { id: Date.now().toString(), text, timestamp: new Date() },
      ...prev,
    ]);
    setAnnouncementText('');
  };

  const selectedLocationData = useMemo(() => {
    if (selectedLocation === 'all') return { name: clubName, address: clubAddress };
    const loc = locations.find(l => l.key === selectedLocation);
    return loc ? { name: loc.name, address: loc.address } : { name: clubName, address: clubAddress };
  }, [selectedLocation, locations, clubName, clubAddress]);

  /* ── No Gym Guard ───────────────────────────────────────── */

  if (!gymId && !gymName) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: c.background, justifyContent: 'center', alignItems: 'center' }} edges={['top']}>
        <Text style={{ color: c.textSecondary, fontSize: 16, textAlign: 'center', padding: 40 }}>
          Club dashboard requires a gym/club association. Set your home gym in profile settings.
        </Text>
      </SafeAreaView>
    );
  }

  /* ── Render ─────────────────────────────────────────────── */

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      {/* Header */}
      <LinearGradient
        colors={[c.background, c.surfaceVariant]}
        style={[styles.header, { borderBottomColor: c.border }]}>
        <TouchableOpacity onPress={() => { if (router.canGoBack()) router.back(); else router.push('/(tabs)/record' as any); }} hitSlop={12}>
          <ChevronLeft size={22} color={c.textSecondary} />
        </TouchableOpacity>
        <View style={{ flex: 1, alignItems: 'center' }}>
          <Text style={[styles.headerTitle, { color: c.accent }]}>CLUB DASHBOARD</Text>
        </View>
        <TouchableOpacity onPress={handleExport} disabled={exporting} hitSlop={12}>
          {exporting ? <ActivityIndicator size="small" color={c.accent} /> : <Download size={20} color={c.accent} />}
        </TouchableOpacity>
      </LinearGradient>

      {loading ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator color={c.accent} size="large" />
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>

          {/* Debug / Error Info */}
          {loadError && (
            <View style={{ backgroundColor: '#4a1c1c', borderRadius: 2, padding: 14, marginBottom: 12 }}>
              <Text style={{ color: '#ff6b6b', fontSize: 13, fontWeight: '700', marginBottom: 4 }}>Error Loading Data</Text>
              <Text style={{ color: '#ff9999', fontSize: 12 }}>{loadError}</Text>
            </View>
          )}
          {__DEV__ && (
            <View style={{ backgroundColor: c.surfaceVariant, borderRadius: 2, padding: 10, marginBottom: 12 }}>
              <Text style={{ color: c.textTertiary, fontSize: 10 }}>
                gymId: {gymId || 'null'} | gymName: {gymName || 'null'} | members: {members.length}
              </Text>
            </View>
          )}

          {/* ── Location Selector (Franchise) ─────────────── */}
          {locations.length > 1 && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.locationScroll} contentContainerStyle={styles.locationScrollContent}>
              <TouchableOpacity
                style={[styles.locationPill, { backgroundColor: selectedLocation === 'all' ? c.accent : c.surface, borderColor: c.border }]}
                onPress={() => setSelectedLocation('all')}>
                <Text style={[styles.locationPillText, { color: selectedLocation === 'all' ? c.background : c.textSecondary }]}>
                  All Locations
                </Text>
              </TouchableOpacity>
              {locations.map(loc => (
                <TouchableOpacity
                  key={loc.key}
                  style={[styles.locationPill, { backgroundColor: selectedLocation === loc.key ? c.accent : c.surface, borderColor: c.border }]}
                  onPress={() => setSelectedLocation(loc.key)}>
                  <Text style={[styles.locationPillText, { color: selectedLocation === loc.key ? c.background : c.textSecondary }]} numberOfLines={1}>
                    {loc.name}
                  </Text>
                  {loc.address ? (
                    <Text style={[styles.locationPillAddr, { color: selectedLocation === loc.key ? c.background : c.textTertiary }]} numberOfLines={1}>
                      {loc.address}
                    </Text>
                  ) : null}
                </TouchableOpacity>
              ))}
            </ScrollView>
          )}

          {/* ── Gym Profile Card ──────────────────────────── */}
          <LinearGradient
            colors={[c.surface, c.surfaceVariant]}
            style={[styles.gymCard, { borderColor: c.border }]}>
            <View style={styles.gymCardHeader}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.gymName, { color: c.text }]}>{selectedLocationData.name}</Text>
                {selectedLocationData.address ? (
                  <View style={styles.gymAddressRow}>
                    <MapPin size={14} color={c.accent} />
                    <Text style={[styles.gymAddress, { color: c.textSecondary }]}>{selectedLocationData.address}</Text>
                  </View>
                ) : null}
              </View>
              <TouchableOpacity style={[styles.editBtn, { borderColor: c.border }]} onPress={() => {}}>
                <Pencil size={14} color={c.textTertiary} />
                <Text style={[styles.editBtnText, { color: c.textTertiary }]}>Edit</Text>
              </TouchableOpacity>
            </View>
          </LinearGradient>

          {/* ── Overview Stats ────────────────────────────── */}
          <View style={styles.statsRow}>
            <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Users size={18} color={c.accent} />
              <Text style={[styles.statValue, { color: c.accent }]}>{stats?.totalMembers ?? 0}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Members</Text>
            </View>
            <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <UserCheck size={18} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>{stats?.activeThisWeek ?? 0}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Active (7d)</Text>
            </View>
            <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Trophy size={18} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>{stats?.avgStrengthScore ?? 0}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Avg Score</Text>
            </View>
          </View>

          <View style={styles.statsRow}>
            <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <BarChart3 size={18} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>{stats?.totalWorkouts ?? 0}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Total Workouts</Text>
            </View>
            <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <TrendingUp size={18} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>{formatNum(stats?.totalVolume ?? 0)}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Total Volume</Text>
            </View>
            <View style={[styles.statCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              <UserX size={18} color={c.accent} />
              <Text style={[styles.statValue, { color: c.text }]}>{(stats?.totalMembers ?? 0) - (stats?.activeThisMonth ?? 0)}</Text>
              <Text style={[styles.statLabel, { color: c.textSecondary }]}>Inactive (30d)</Text>
            </View>
          </View>

          {/* ── Announcements ─────────────────────────────── */}
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.cardTitle, { color: c.textTertiary }]}>ANNOUNCEMENTS</Text>
            <View style={styles.announcementInput}>
              <TextInput
                style={[styles.announcementTextInput, { color: c.text, backgroundColor: c.surfaceVariant, borderColor: c.border }]}
                placeholder="Write a message to your members..."
                placeholderTextColor={c.textTertiary}
                value={announcementText}
                onChangeText={setAnnouncementText}
                multiline
              />
              <TouchableOpacity
                style={[styles.sendBtn, { backgroundColor: announcementText.trim() ? c.accent : c.surfaceVariant }]}
                onPress={handleSendAnnouncement}
                disabled={!announcementText.trim()}>
                <Send size={16} color={announcementText.trim() ? c.background : c.textTertiary} />
                <Text style={[styles.sendBtnText, { color: announcementText.trim() ? c.background : c.textTertiary }]}>
                  Send to All Members
                </Text>
              </TouchableOpacity>
            </View>
            {announcements.length > 0 && (
              <AnnouncementList announcements={announcements} colors={c} />
            )}
          </View>

          {/* ── Challenge Ideas ───────────────────────────── */}
          <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
            <Text style={[styles.cardTitle, { color: c.textTertiary }]}>CHALLENGE IDEAS</Text>
            {CHALLENGE_TEMPLATES.map((ch, i) => {
              const Icon = ch.icon;
              return (
                <TouchableOpacity
                  key={i}
                  style={[styles.challengeCard, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}
                  activeOpacity={0.7}
                  onPress={() => {
                    setAnnouncementText(`New Challenge: ${ch.title}! ${ch.reward}. Compete now in the DUEL app!`);
                  }}>
                  <Icon size={20} color={c.accent} />
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.challengeTitle, { color: c.text }]}>{ch.title}</Text>
                    <Text style={[styles.challengeReward, { color: c.textSecondary }]}>{ch.reward}</Text>
                  </View>
                </TouchableOpacity>
              );
            })}
            <TouchableOpacity style={[styles.customChallengeBtn, { borderColor: c.accent }]} activeOpacity={0.7}>
              <Plus size={16} color={c.accent} />
              <Text style={[styles.customChallengeBtnText, { color: c.accent }]}>Create Custom Challenge</Text>
            </TouchableOpacity>
          </View>

          {/* ── Popular Exercises ─────────────────────────── */}
          {stats && stats.topExercises.length > 0 && (
            <View style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}>
              <Text style={[styles.cardTitle, { color: c.textTertiary }]}>POPULAR EXERCISES</Text>
              {stats.topExercises.map((ex, i) => (
                <View key={ex.name} style={styles.exerciseRow}>
                  <Text style={[styles.exerciseRank, { color: c.textTertiary }]}>{i + 1}</Text>
                  <Text style={[styles.exerciseName, { color: c.text }]}>{ex.name}</Text>
                  <Text style={[styles.exerciseCount, { color: c.accent }]}>{ex.count}x</Text>
                </View>
              ))}
            </View>
          )}

          {/* ── Member List ───────────────────────────────── */}
          <Text style={[styles.sectionTitle, { color: c.text }]}>Members ({filteredMembers.length})</Text>

          <View style={styles.filterRow}>
            <View style={[styles.searchBox, { backgroundColor: c.surfaceVariant, borderColor: c.border }]}>
              <Search size={16} color={c.textTertiary} />
              <TextInput
                style={[styles.searchInput, { color: c.text }]}
                placeholder="Search members..."
                placeholderTextColor={c.textTertiary}
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
            </View>
          </View>

          <View style={styles.sortRow}>
            {(['strength', 'volume', 'workouts', 'name'] as const).map(s => (
              <TouchableOpacity
                key={s}
                style={[styles.sortPill, { backgroundColor: sortBy === s ? c.accent : c.surfaceVariant }]}
                onPress={() => setSortBy(s)}>
                <Text style={[styles.sortPillText, { color: sortBy === s ? c.background : c.textSecondary }]}>
                  {s === 'strength' ? 'Score' : s.charAt(0).toUpperCase() + s.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {filteredMembers.map(m => {
            const score = m.duel_standard_strength_kg ?? m.lifting_score ?? 0;
            const daysSinceWorkout = m.lastWorkoutDate
              ? Math.floor((Date.now() - new Date(m.lastWorkoutDate).getTime()) / 86400000)
              : null;
            const isInactive = daysSinceWorkout === null || daysSinceWorkout > 14;

            return (
              <View key={m.id} style={[styles.memberCard, { backgroundColor: c.surface, borderColor: c.border }]}>
                <View style={styles.memberHeader}>
                  <View style={[styles.memberAvatar, { backgroundColor: c.surfaceVariant }]}>
                    <Text style={[styles.memberInitial, { color: c.textSecondary }]}>
                      {getMemberName(m).charAt(0).toUpperCase()}
                    </Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.memberName, { color: c.text }]}>{getMemberName(m)}</Text>
                    <Text style={[styles.memberHandle, { color: c.textTertiary }]}>
                      @{m.handle || '--'} · {m.gender || '--'}
                    </Text>
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={[styles.memberScore, { color: c.accent }]}>{score > 0 ? score : '--'}</Text>
                    <Text style={[styles.memberScoreLabel, { color: c.textTertiary }]}>Score</Text>
                  </View>
                </View>
                <View style={styles.memberStats}>
                  <View style={styles.memberStatItem}>
                    <Text style={[styles.memberStatValue, { color: c.text }]}>{m.workoutCount}</Text>
                    <Text style={[styles.memberStatLabel, { color: c.textTertiary }]}>Workouts</Text>
                  </View>
                  <View style={styles.memberStatItem}>
                    <Text style={[styles.memberStatValue, { color: c.text }]}>{formatNum(m.totalVolume)}</Text>
                    <Text style={[styles.memberStatLabel, { color: c.textTertiary }]}>Volume</Text>
                  </View>
                  <View style={styles.memberStatItem}>
                    <Text style={[styles.memberStatValue, { color: isInactive ? '#ff6b6b' : c.text }]}>
                      {daysSinceWorkout !== null ? `${daysSinceWorkout}d` : 'Never'}
                    </Text>
                    <Text style={[styles.memberStatLabel, { color: c.textTertiary }]}>Last Active</Text>
                  </View>
                </View>
              </View>
            );
          })}

          <View style={{ height: 40 }} />
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

/* ── Styles ──────────────────────────────────────────────── */

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 14, borderBottomWidth: 1,
  },
  headerTitle: { fontSize: 18, fontWeight: '900', letterSpacing: 1 },
  content: { padding: 16 },

  // Location pills
  locationScroll: { marginBottom: 16 },
  locationScrollContent: { gap: 8, paddingRight: 8 },
  locationPill: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 4, borderWidth: 1, minWidth: 80, alignItems: 'center' },
  locationPillText: { fontSize: 13, fontWeight: '700' },
  locationPillAddr: { fontSize: 10, fontWeight: '500', marginTop: 2 },

  // Gym profile
  gymCard: { borderWidth: 1, borderRadius: 2, padding: 20, marginBottom: 16 },
  gymCardHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between' },
  gymName: { fontSize: 22, fontWeight: '900', marginBottom: 6 },
  gymAddressRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  gymAddress: { fontSize: 13, fontWeight: '500', flex: 1 },
  editBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderRadius: 2, paddingHorizontal: 10, paddingVertical: 6 },
  editBtnText: { fontSize: 12, fontWeight: '600' },

  // Stats
  statsRow: { flexDirection: 'row', gap: 10, marginBottom: 10 },
  statCard: { flex: 1, borderWidth: 1, borderRadius: 2, padding: 14, alignItems: 'center', gap: 4 },
  statValue: { fontSize: 22, fontWeight: '900' },
  statLabel: { fontSize: 10, fontWeight: '600', textAlign: 'center' },

  // Cards
  card: { borderWidth: 1, borderRadius: 2, padding: 16, marginBottom: 16, marginTop: 6 },
  cardTitle: { fontSize: 11, fontWeight: '700', letterSpacing: 1, marginBottom: 12 },

  // Announcements
  announcementInput: { gap: 10 },
  announcementTextInput: {
    borderWidth: 1, borderRadius: 2, padding: 12, fontSize: 14, minHeight: 60, textAlignVertical: 'top',
  },
  sendBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 2 },
  sendBtnText: { fontSize: 14, fontWeight: '700' },
  announcementList: { marginTop: 14, gap: 8 },
  announcementItem: { borderTopWidth: 1, paddingTop: 10 },
  announcementItemText: { fontSize: 14, fontWeight: '500', marginBottom: 4 },
  announcementItemTime: { fontSize: 11, fontWeight: '500' },

  // Challenges
  challengeCard: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderRadius: 2, borderWidth: 1, marginBottom: 8 },
  challengeTitle: { fontSize: 14, fontWeight: '700' },
  challengeReward: { fontSize: 12, fontWeight: '500', marginTop: 2 },
  customChallengeBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 2, borderWidth: 1, borderStyle: 'solid', marginTop: 4 },
  customChallengeBtnText: { fontSize: 14, fontWeight: '700' },

  // Exercises
  exerciseRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, gap: 10 },
  exerciseRank: { fontSize: 12, fontWeight: '700', width: 24, textAlign: 'center' },
  exerciseName: { fontSize: 14, fontWeight: '600', flex: 1 },
  exerciseCount: { fontSize: 14, fontWeight: '800' },

  // Members
  sectionTitle: { fontSize: 18, fontWeight: '900', marginBottom: 12, marginTop: 12 },
  filterRow: { marginBottom: 10 },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 12, paddingVertical: 10,
    borderRadius: 2, borderWidth: 1,
  },
  searchInput: { flex: 1, fontSize: 14 },
  sortRow: { flexDirection: 'row', gap: 8, marginBottom: 16 },
  sortPill: { paddingHorizontal: 14, paddingVertical: 6, borderRadius: 2 },
  sortPillText: { fontSize: 12, fontWeight: '700' },
  memberCard: { borderWidth: 1, borderRadius: 2, padding: 14, marginBottom: 10 },
  memberHeader: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 10 },
  memberAvatar: { width: 40, height: 40, borderRadius: 4, justifyContent: 'center', alignItems: 'center' },
  memberInitial: { fontSize: 16, fontWeight: '800' },
  memberName: { fontSize: 15, fontWeight: '700' },
  memberHandle: { fontSize: 12, fontWeight: '500' },
  memberScore: { fontSize: 20, fontWeight: '900' },
  memberScoreLabel: { fontSize: 10, fontWeight: '600' },
  memberStats: { flexDirection: 'row', justifyContent: 'space-around' },
  memberStatItem: { alignItems: 'center' },
  memberStatValue: { fontSize: 14, fontWeight: '700' },
  memberStatLabel: { fontSize: 10, fontWeight: '600' },
});
